import * as React from 'react'
import Box from '@mui/material/Box'
import Stepper from '@mui/material/Stepper'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Button from '@mui/material/Button'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import GeneralDetails from './GeneralDetails'
import { useTheme } from '@mui/material'
import useCustomStyles from '../../../../../utils/hooks/CustomStylesHook'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import AssessmentDetails from './AssessmentDetails'
import ReviewDetails from './ReviewDetails'
import PreviousMedications from './PreviousMedications'
import CloudDoneIcon from '@mui/icons-material/CloudDone'
import {
  getCompositionByPatientId,
  postBreathingObservation,
  postGcsObservation,
  postGeneralExaminationObservation,
  postGeneralInitialObservation,
  saveComposition,
} from '../../../../../services/InitialAssessmentService'
import {
  DECISION_OPTIONS,
  ERAdvice,
  InitialFormState,
  Medication,
  OBSTETRIC_RADIO_OPTIONS,
  REVIEW_OF_SYSTEMS_CONST,
} from './Constants'
import { toast } from 'react-toastify'
import { useAppSelector } from '../../../../../app/hooks'
import { selectId } from '../../IpdVisitSlice'
import { useSearchParams } from 'react-router-dom'

const styles = () => ({
  initialAssessmentContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  stepperContainer: {
    width: '80%',
  },
  initialAssessmentStepper: {
    marginBottom: '4%',
  },
  initialAssessmentButton: {
    backgroundColor: '#4682b4',
    textTransform: 'capitalize',
  },
})

const steps = ['1', '2', '3', '4']

export default function InitialAssessment() {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)
  const [searchParams] = useSearchParams()
  const id = (useAppSelector(selectId) || searchParams.get('patientId')) ?? ''

  const [compositionId, setCompositionId] = React.useState('')
  const [observationOneId, setObservationOneId] = React.useState('')
  const [observationTwoId, setObservationTwoId] = React.useState('')
  const [observationThreeId, setObservationThreeId] = React.useState('')
  // eslint-disable-next-line
  const [observationFourId, setObservationFourId] = React.useState('')
  const [medicationStatementIdOne, setMedicationStatementIdOne] =
    React.useState('')
  const [medicationStatementIdTwo, setMedicationStatementIdTwo] =
    React.useState('')

  const [isStepOneSubmit, setIsStepOneSubmit] = React.useState(false)
  const [isStepTwoSubmit, setIsStepTwoSubmit] = React.useState(false)
  const [isStepThreeSubmit, setIsStepThreeSubmit] = React.useState(false)
  const [isStepFourSubmit, setIsStepFourSubmit] = React.useState(false)

  const [activeStep, setActiveStep] = React.useState(0)

  const initialFormState: InitialFormState = {
    mlc: false,
    broughtBy: { name: '', relationship: '', phoneNumber: '' },
    arrivalMode: '',
    arrivalNotification: '',
    hospitalName: '',
    levelOfConsciousness: '',
    pregnant: '',
    allergies: '',
    presentingComplaints: '',
    provisionalDiagnosis: '',
    vitals: {
      pulse: '',
      spo2: '',
      bp: '',
      rr: '',
      grbs: '',
      temperature: '',
      gcs: '',
      painScore: '',
    },
    comorbidities: {
      htn: false,
      dm: false,
      cva: false,
      epilepsy: false,
      cad: false,
      copd: false,
      ckd: false,
      obesity: false,
    },
    assessment: {
      airway: '',
      primarySurveyAirway: '',
      other: '',
      cervicalSpineImmobilization: '',
      primarySurveyCervicalSpineImmobilization: '',
      breathing: {
        o2: false,
        nrbm: false,
        niv: false,
        invasiveVentilation: false,
        o2PrimarySurvey: '',
        nrbmPrimarySurvey: '',
        nivPrimarySurvey: '',
        invasiveVentilationPrimarySurvey: '',
      },
      circulation: {
        peripheralPulses: {
          carotidRight: '',
          carotidLeft: '',
          brachialRight: '',
          brachialLeft: '',
          radialRight: '',
          radialLeft: '',
          femoralRight: '',
          femoralLeft: '',
          poplitealRight: '',
          poplitealLeft: '',
          tibalRight: '',
          tibalLeft: '',
          dorasilRight: '',
          dorasilLeft: '',
        },
        capillaryRefillTime: '',
      },
    },
    reviewOfSystems: {},
    gcs: {
      e: '',
      v: '',
      m: '',
    },
    pupils: { right: '', left: '' },
    fnd: '',
    exposure: '',
    chiefComplaints: '',
    previousMedications: [
      {
        drug: '',
        dose: '',
        frequency: '',
        lastDoseDate: '',
        lastDoseTime: '',
      },
    ],
    consultation: {
      department: '',
      timeOfInitiation: '',
      timeOfArrival: '',
      notes: '',
    },
    generalExamination: {
      pallor: '',
      icterus: '',
      cyanosis: '',
      clubbing: '',
      lymphadenopathy: '',
      edema: '',
    },
    obstetric: {
      obstetricValue: '',
      obstetricOptions: '',
      obstetricOptionDetails: '',
      gravidity: '',
      parity: '',
      parityDetails: '',
      lastMenstrualPeriod: '',
      days: '',
      historyOfContraception: '',
      contraceptionDetails: '',
      fertilityTreatment: '',
    },
    erAdvice: [{ drug: '', medications: '' }],
  }

  const initialAssessmentForm = useFormik({
    initialValues: initialFormState,
    validationSchema: Yup.object().shape({
      mlc: Yup.boolean(),
      broughtBy: Yup.object().shape({
        name: Yup.string(),
        relationship: Yup.string(),
        phoneNumber: Yup.string(),
      }),
      arrivalMode: Yup.string(),
      arrivalNotification: Yup.string(),
      hospitalName: Yup.string(),
      levelOfConsciousness: Yup.string(),
      pregnant: Yup.string(),
      allergies: Yup.string(),
      presentingComplaints: Yup.string(),
      provisionalDiagnosis: Yup.string(),
      vitals: Yup.object().shape({
        pulse: Yup.string(),
        spo2: Yup.string(),
        bp: Yup.string(),
        rr: Yup.string(),
        grbs: Yup.string(),
        temperature: Yup.string(),
        gcs: Yup.string(),
        painScore: Yup.string(),
      }),
      comorbidities: Yup.object().shape({
        htn: Yup.boolean(),
        dm: Yup.boolean(),
        cva: Yup.boolean(),
        epilepsy: Yup.boolean(),
        cad: Yup.boolean(),
        copd: Yup.boolean(),
        ckd: Yup.boolean(),
        obesity: Yup.boolean(),
      }),
      assessment: Yup.object().shape({
        airway: Yup.string(),
        primarySurveyAirway: Yup.string(),
        other: Yup.string(),
        cervicalSpineImmobilization: Yup.string(),
        primarySurveyCervicalSpineImmobilization: Yup.string(),
        breathing: Yup.object().shape({
          o2: Yup.boolean(),
          nrbm: Yup.boolean(),
          niv: Yup.boolean(),
          invasiveVentilation: Yup.boolean(),
          o2PrimarySurvey: Yup.string(),
          nrbmPrimarySurvey: Yup.string(),
          nivPrimarySurvey: Yup.string(),
          invasiveVentilationPrimarySurvey: Yup.string(),
        }),
        circulation: Yup.object().shape({
          peripheralPulses: Yup.object(),
          capillaryRefillTime: Yup.string(),
        }),
      }),
      reviewOfSystems: Yup.object(),
      previousMedications: Yup.array().of(
        Yup.object().shape({
          drug: Yup.string(),
          dose: Yup.string(),
          frequency: Yup.string(),
          lastDoseDate: Yup.string(),
          lastDoseTime: Yup.string(),
        })
      ),
      pupils: Yup.object().shape({
        right: Yup.string(),
        left: Yup.string(),
      }),
      erAdvice: Yup.array().of(
        Yup.object().shape({
          lab: Yup.string(),
          medications: Yup.string(),
        })
      ),
      obstetric: Yup.object().shape({
        obstetricValue: Yup.string(),
        obstetricOptions: Yup.string(),
        obstetricOptionDetails: Yup.string(),
        gravidity: Yup.number(),
        parity: Yup.string(),
        parityDetails: Yup.string(),
        lastMenstrualPeriod: Yup.date(),
        days: Yup.number(),
        historyOfContraception: Yup.string(),
        contraceptionDetails: Yup.string(),
        fertilityTreatment: Yup.string(),
      }),
    }),
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(false)
      handleSubmit(values, setSubmitting)
    },
    enableReinitialize: true,
  })

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      setActiveStep(0)
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const renderStep = () => {
    switch (activeStep) {
      case 0:
        return <GeneralDetails form={initialAssessmentForm} />

      case 1:
        return <AssessmentDetails form={initialAssessmentForm} />

      case 2:
        return <PreviousMedications form={initialAssessmentForm} />

      case 3:
        return <ReviewDetails form={initialAssessmentForm} />

      default:
        return <div>1</div>
    }
  }

  const getFormData = React.useCallback(async () => {
    const response = await getCompositionByPatientId(id)
    if (response?.success) {
      setCompositionId(response?.data?.compositionId)
      setObservationOneId(
        response?.data?.generalInitialObservation?.observationId
      )
      setIsStepOneSubmit(
        response?.data?.generalInitialObservation?.isCompleted ? true : false
      )
      setIsStepTwoSubmit(
        response?.data?.breathingInitialObservation?.isCompleted ? true : false
      )
      setObservationTwoId(
        response?.data?.breathingInitialObservation?.observationId
      )

      setIsStepThreeSubmit(
        response?.data?.gcsObservationDTO?.isCompleted ? true : false
      )
      setObservationThreeId(response?.data?.gcsObservationDTO?.observationId)
      setMedicationStatementIdOne(
        response?.data?.gcsObservationDTO?.previousMedications?.[0]
          ?.previousMedicationId
      )
      setMedicationStatementIdTwo(
        response?.data?.gcsObservationDTO?.erAdvice?.[0]?.erAdviceId
      )

      setIsStepFourSubmit(
        response?.data?.generalExaminationObservationDTO?.isCompleted
          ? true
          : false
      )

      initialAssessmentForm?.setValues({
        mlc: response?.data?.generalInitialObservation?.mlc || false,
        broughtBy: {
          name: response?.data?.generalInitialObservation?.broughtByName || '',
          relationship:
            response?.data?.generalInitialObservation?.broughtByRelationship ||
            '',
          phoneNumber:
            response?.data?.generalInitialObservation?.broughtByNumber || '',
        },
        arrivalMode:
          response?.data?.generalInitialObservation?.arrivalMode || '',
        arrivalNotification: response?.data?.generalInitialObservation
          ?.arrivalNotification
          ? DECISION_OPTIONS?.[0]?.value
          : DECISION_OPTIONS?.[1]?.value || '',
        hospitalName:
          response?.data?.generalInitialObservation?.hospitalName || '',
        levelOfConsciousness:
          response?.data?.generalInitialObservation?.levelOfConsciousness || '',
        pregnant: response?.data?.generalInitialObservation?.pregnant
          ? DECISION_OPTIONS?.[0]?.value
          : DECISION_OPTIONS?.[1]?.value || '',
        allergies: response?.data?.generalInitialObservation?.allergies || '',
        presentingComplaints:
          response?.data?.generalInitialObservation?.presentingComplaints || '',
        provisionalDiagnosis:
          response?.data?.generalExaminationObservationDTO
            ?.provisionalDiagnosis || '',
        vitals: {
          pulse: response?.data?.generalInitialObservation?.pulse || '',
          spo2: response?.data?.generalInitialObservation?.spo2 || '',
          bp: response?.data?.generalInitialObservation?.bp || '',
          rr: response?.data?.generalInitialObservation?.rr || '',
          grbs: response?.data?.generalInitialObservation?.grbs || '',
          temperature:
            response?.data?.generalInitialObservation?.temperature || '',
          gcs: response?.data?.generalInitialObservation?.gcs || '',
          painScore: response?.data?.generalInitialObservation?.painScore || '',
        },
        comorbidities: {
          htn: response?.data?.generalInitialObservation?.htn || false,
          dm: response?.data?.generalInitialObservation?.dm || false,
          cva: response?.data?.generalInitialObservation?.cva || false,
          epilepsy:
            response?.data?.generalInitialObservation?.epilepsy || false,
          cad: response?.data?.generalInitialObservation?.cad || false,
          copd: response?.data?.generalInitialObservation?.copd || false,
          ckd: response?.data?.generalInitialObservation?.ckd || false,
          obesity: response?.data?.generalInitialObservation?.obesity || false,
        },
        assessment: {
          airway: response?.data?.breathingInitialObservation?.airway || '',
          primarySurveyAirway:
            response?.data?.breathingInitialObservation?.airwayPrimarySurvey ||
            '',
          other: response?.data?.breathingInitialObservation?.other || '',
          cervicalSpineImmobilization:
            response?.data?.breathingInitialObservation
              ?.cervicalSpineImmobilization || '',
          primarySurveyCervicalSpineImmobilization:
            response?.data?.breathingInitialObservation
              ?.cervicalSpineImmobilizationSurvey || '',
          breathing: {
            o2: response?.data?.breathingInitialObservation?.o2 ?? false,
            nrbm: response?.data?.breathingInitialObservation?.nrbm ?? false,
            niv: response?.data?.breathingInitialObservation?.niv ?? false,
            invasiveVentilation:
              response?.data?.breathingInitialObservation
                ?.invasiveVentilation ?? false,
            o2PrimarySurvey:
              response?.data?.breathingInitialObservation?.o2PrimarySurvey ||
              '',
            nrbmPrimarySurvey:
              response?.data?.breathingInitialObservation?.nrbmPrimarySurvey ||
              '',
            nivPrimarySurvey:
              response?.data?.breathingInitialObservation?.nivPrimarySurvey ||
              '',
            invasiveVentilationPrimarySurvey:
              response?.data?.breathingInitialObservation
                ?.invasiveVentilationPrimarySurvey || '',
          },
          circulation: {
            peripheralPulses: {
              carotidRight:
                response?.data?.breathingInitialObservation?.carotidR || '',
              carotidLeft:
                response?.data?.breathingInitialObservation?.carotidL || '',
              brachialRight:
                response?.data?.breathingInitialObservation?.brachialR || '',
              brachialLeft:
                response?.data?.breathingInitialObservation?.brachialL || '',
              radialRight:
                response?.data?.breathingInitialObservation?.radialR || '',
              radialLeft:
                response?.data?.breathingInitialObservation?.radialL || '',
              femoralRight:
                response?.data?.breathingInitialObservation?.femoralR || '',
              femoralLeft:
                response?.data?.breathingInitialObservation?.femoralL || '',
              poplitealRight:
                response?.data?.breathingInitialObservation?.poplitealR || '',
              poplitealLeft:
                response?.data?.breathingInitialObservation?.poplitealL || '',
              tibalRight:
                response?.data?.breathingInitialObservation?.tibalR || '',
              tibalLeft:
                response?.data?.breathingInitialObservation?.tibalL || '',
              dorasilRight:
                response?.data?.breathingInitialObservation?.dorasilR || '',
              dorasilLeft:
                response?.data?.breathingInitialObservation?.dorasilL || '',
            },
            capillaryRefillTime:
              response?.data?.breathingInitialObservation
                ?.capillaryRefillTime || '',
          },
        },
        reviewOfSystems: {
          Gastrointestinal: {
            malena:
              response?.data?.generalExaminationObservationDTO?.malena || false,
            hepatitisBC:
              response?.data?.generalExaminationObservationDTO?.hepatitisBC ||
              false,
            diarrhea:
              response?.data?.generalExaminationObservationDTO?.diarrhea ||
              false,
            hematemesis:
              response?.data?.generalExaminationObservationDTO?.hematemesis ||
              false,
            flatus:
              response?.data?.generalExaminationObservationDTO?.flatus || false,
            jaundice:
              response?.data?.generalExaminationObservationDTO?.jaundice ||
              false,
            constipation:
              response?.data?.generalExaminationObservationDTO?.constipation ||
              false,
            bleedingPR:
              response?.data?.generalExaminationObservationDTO?.bleedingPR ||
              false,
            anyOther:
              response?.data?.generalExaminationObservationDTO?.giAnyOther ||
              false,
            noSymptoms:
              response?.data?.generalExaminationObservationDTO?.giNoSymptoms ||
              false,
            abdominalPain:
              response?.data?.generalExaminationObservationDTO?.abdominalPain ||
              false,
            vomiting:
              response?.data?.generalExaminationObservationDTO?.vomiting ||
              false,
          },
          Genitourinary: {
            hematuria:
              response?.data?.generalExaminationObservationDTO?.guHematuria ||
              false,
            historyOfTURP:
              response?.data?.generalExaminationObservationDTO?.guHoTurp ||
              false,
            dialysis:
              response?.data?.generalExaminationObservationDTO?.guDialysis ||
              false,
            penileVaginalDischarge:
              response?.data?.generalExaminationObservationDTO?.guDischarge ||
              false,
            dysuria:
              response?.data?.generalExaminationObservationDTO?.guDysuria ||
              false,
            historyOfStones:
              response?.data?.generalExaminationObservationDTO?.guHoStones ||
              false,
            infertility:
              response?.data?.generalExaminationObservationDTO?.guInfertility ||
              false,
            nocturia:
              response?.data?.generalExaminationObservationDTO?.guNocturia ||
              false,
            renalFailure:
              response?.data?.generalExaminationObservationDTO
                ?.guRenalFailure || false,
            anyOther:
              response?.data?.generalExaminationObservationDTO?.guAnyOther ||
              false,
            noSymptoms:
              response?.data?.generalExaminationObservationDTO?.guNoSymptoms ||
              false,
          },
          Musculoskeletal: {
            jointPain:
              response?.data?.generalExaminationObservationDTO?.msJointPain ||
              false,
            jointSwelling:
              response?.data?.generalExaminationObservationDTO
                ?.msJointSwelling || false,
            inabilityToWalk:
              response?.data?.generalExaminationObservationDTO
                ?.msInabilityToWalk || false,
            musclePain:
              response?.data?.generalExaminationObservationDTO?.msMusclePain ||
              false,
            fracture:
              response?.data?.generalExaminationObservationDTO?.msFracture ||
              false,
            jointStiffness:
              response?.data?.generalExaminationObservationDTO
                ?.msJointStiffness || false,
            trauma:
              response?.data?.generalExaminationObservationDTO?.msTrauma ||
              false,
            noSymptoms:
              response?.data?.generalExaminationObservationDTO?.msNoSymptoms ||
              false,
          },
          Neurological: {
            headache:
              response?.data?.generalExaminationObservationDTO?.neuroHeadache ||
              false,
            earDischarge:
              response?.data?.generalExaminationObservationDTO
                ?.neuroEarDischarge || false,
            tinnitus:
              response?.data?.generalExaminationObservationDTO?.neuroTinnitus ||
              false,
            vertigo:
              response?.data?.generalExaminationObservationDTO?.neuroVertigo ||
              false,
            numbness:
              response?.data?.generalExaminationObservationDTO?.neuroNumbness ||
              false,
            syncope:
              response?.data?.generalExaminationObservationDTO?.neuroSyncope ||
              false,
            backAche:
              response?.data?.generalExaminationObservationDTO?.neuroBackAche ||
              false,
            diplopia:
              response?.data?.generalExaminationObservationDTO?.neuroDiplopia ||
              false,
            radiatingPain:
              response?.data?.generalExaminationObservationDTO
                ?.neuroRadiatingPain || false,
            drowsiness:
              response?.data?.generalExaminationObservationDTO
                ?.neuroDrowsiness || false,
            weaknessInLimbs:
              response?.data?.generalExaminationObservationDTO
                ?.neuroWeaknessLimbs || false,
            stroke:
              response?.data?.generalExaminationObservationDTO?.neuroStroke ||
              false,
            claudication:
              response?.data?.generalExaminationObservationDTO
                ?.neuroClaudication || false,
            restPainInLegs:
              response?.data?.generalExaminationObservationDTO
                ?.neuroRestPainLegs || false,
            noSymptoms:
              response?.data?.generalExaminationObservationDTO
                ?.neuroNoSymptoms || false,
          },
          Integumentary: {
            skinDisorders:
              response?.data?.generalExaminationObservationDTO
                ?.integSkinDisorders || false,
            rash:
              response?.data?.generalExaminationObservationDTO?.integRash ||
              false,
            itching:
              response?.data?.generalExaminationObservationDTO?.integItching ||
              false,
            discoloration:
              response?.data?.generalExaminationObservationDTO
                ?.integDiscoloration || false,
            others:
              response?.data?.generalExaminationObservationDTO?.integOthers ||
              false,
            noSymptoms:
              response?.data?.generalExaminationObservationDTO
                ?.integNoSymptoms || false,
          },
          Psychiatric: {
            agitated:
              response?.data?.generalExaminationObservationDTO?.psyAgitated ||
              false,
            demented:
              response?.data?.generalExaminationObservationDTO?.psyDemented ||
              false,
            anxious:
              response?.data?.generalExaminationObservationDTO?.psyAnxious ||
              false,
            depression:
              response?.data?.generalExaminationObservationDTO?.psyDepression ||
              false,
            noSymptoms:
              response?.data?.generalExaminationObservationDTO?.psyNoSymptoms ||
              false,
          },
          'Endocrine/immunologic': {
            thyroidSwelling:
              response?.data?.generalExaminationObservationDTO
                ?.endoThyroidSwelling || false,
            diabetes:
              response?.data?.generalExaminationObservationDTO?.endoDiabetes ||
              false,
            adrenalMass:
              response?.data?.generalExaminationObservationDTO
                ?.endoAdrenalMass || false,
            pituitaryLesion:
              response?.data?.generalExaminationObservationDTO
                ?.endoPituitaryLesion || false,
            noSymptoms:
              response?.data?.generalExaminationObservationDTO
                ?.endoNoSymptoms || false,
          },
          'Hematologic/Lymphatic': {
            anemia:
              response?.data?.generalExaminationObservationDTO?.hemAnemia ||
              false,
            lowPlatelets:
              response?.data?.generalExaminationObservationDTO
                ?.hemLowPlatelets || false,
            neutropenia:
              response?.data?.generalExaminationObservationDTO
                ?.hemNeutropenia || false,
            bloodTransfusions:
              response?.data?.generalExaminationObservationDTO
                ?.hemBloodTransfusions || false,
            noSymptoms:
              response?.data?.generalExaminationObservationDTO?.hemNoSymptoms ||
              false,
          },
          ENT: {
            earAche:
              response?.data?.generalExaminationObservationDTO?.earAche ||
              false,
            earDischarge:
              response?.data?.generalExaminationObservationDTO?.earDischarge ||
              false,
            tinnitus:
              response?.data?.generalExaminationObservationDTO?.tinnitus ||
              false,
            vertigo:
              response?.data?.generalExaminationObservationDTO?.vertigo ||
              false,
            voiceChange:
              response?.data?.generalExaminationObservationDTO?.voiceChange ||
              false,
            soreThroat:
              response?.data?.generalExaminationObservationDTO?.soreThroat ||
              false,
            swellingNeck:
              response?.data?.generalExaminationObservationDTO?.swellingNeck ||
              false,
            difficultySwallowing:
              response?.data?.generalExaminationObservationDTO
                ?.difficultyInSwallowing || false,
            noSymptoms:
              response?.data?.generalExaminationObservationDTO?.entNoSymptoms ||
              false,
          },
          Ophthalmology: {
            eyeRedness:
              response?.data?.generalExaminationObservationDTO?.eyeRedness ||
              false,
            visionLoss:
              response?.data?.generalExaminationObservationDTO?.visionLoss ||
              false,
            eyePain:
              response?.data?.generalExaminationObservationDTO?.eyePain ||
              false,
            eyeDischarge:
              response?.data?.generalExaminationObservationDTO?.eyeDischarge ||
              false,
            noSymptoms:
              response?.data?.generalExaminationObservationDTO
                ?.ophthalmologyNoSymptoms || false,
          },
          Cardiovascular: {
            shortnessOfBreath:
              response?.data?.generalExaminationObservationDTO
                ?.shortnessOfBreath || false,
            chestPain:
              response?.data?.generalExaminationObservationDTO?.chestPain ||
              false,
            oedema:
              response?.data?.generalExaminationObservationDTO?.oedema || false,
            palpitation:
              response?.data?.generalExaminationObservationDTO?.palpitation ||
              false,
            noSymptoms:
              response?.data?.generalExaminationObservationDTO?.cvNoSymptoms ||
              false,
          },
          Respiratory: {
            hemoptysis:
              response?.data?.generalExaminationObservationDTO?.hemoptysis ||
              false,
            cough:
              response?.data?.generalExaminationObservationDTO?.cough || false,
            sputum:
              response?.data?.generalExaminationObservationDTO?.sputum || false,
            wheeze:
              response?.data?.generalExaminationObservationDTO?.wheeze || false,
            noSymptoms:
              response?.data?.generalExaminationObservationDTO
                ?.respNoSymptoms || false,
          },
        },
        gcs: {
          e: response?.data?.gcsObservationDTO?.gcsE,
          v: response?.data?.gcsObservationDTO?.gcsV,
          m: response?.data?.gcsObservationDTO?.gcsM,
        },
        pupils: {
          right: response?.data?.gcsObservationDTO?.pupilsRight,
          left: response?.data?.gcsObservationDTO?.pupilsLeft,
        },
        fnd: response?.data?.gcsObservationDTO?.fnd,
        exposure: response?.data?.gcsObservationDTO?.exposure,
        chiefComplaints: response?.data?.gcsObservationDTO?.chiefComplaints,
        previousMedications:
          response?.data?.gcsObservationDTO?.previousMedications?.map(
            (item: Medication) => ({
              drug: item?.drug || '',
              dose: item?.dose || '',
              frequency: item?.frequency || '',
              lastDoseDate: item?.lastDoseDate || '',
              lastDoseTime: item?.lastDoseTime || '',
            })
          ) || [],
        consultation: {
          department: '',
          timeOfInitiation: '',
          timeOfArrival: '',
          notes: '',
        },
        generalExamination: {
          pallor:
            response?.data?.generalExaminationObservationDTO?.pallor || '',
          icterus:
            response?.data?.generalExaminationObservationDTO?.icterus || '',
          cyanosis:
            response?.data?.generalExaminationObservationDTO?.cyanosis || '',
          clubbing:
            response?.data?.generalExaminationObservationDTO?.clubbing || '',
          lymphadenopathy:
            response?.data?.generalExaminationObservationDTO?.lymphadenopathy ||
            '',
          edema: response?.data?.generalExaminationObservationDTO?.edema || '',
        },
        obstetric: {
          obstetricValue: response?.data?.generalExaminationObservationDTO
            ?.obstetricYes
            ? OBSTETRIC_RADIO_OPTIONS?.[0]?.value
            : OBSTETRIC_RADIO_OPTIONS?.[1]?.value || '',
          obstetricOptions:
            response?.data?.generalExaminationObservationDTO
              ?.obstetricDeliveryType || '',
          obstetricOptionDetails:
            response?.data?.generalExaminationObservationDTO
              ?.obstetricDeliveryDetails || '',
          gravidity:
            response?.data?.generalExaminationObservationDTO
              ?.obstetricGravidity || '',
          parity:
            response?.data?.generalExaminationObservationDTO?.obstetricParity ||
            '',
          parityDetails:
            response?.data?.generalExaminationObservationDTO
              ?.obstetricParityDetails || '',
          lastMenstrualPeriod:
            response?.data?.generalExaminationObservationDTO?.obstetricLmp ||
            '',
          days:
            response?.data?.generalExaminationObservationDTO
              ?.obstetricLmpDays || '',
          historyOfContraception: response?.data
            ?.generalExaminationObservationDTO?.obstetricContraceptionHistory
            ? DECISION_OPTIONS?.[0]?.value
            : DECISION_OPTIONS?.[1]?.value || '',
          contraceptionDetails:
            response?.data?.generalExaminationObservationDTO
              ?.obstetricContraceptionDetails || '',
          fertilityTreatment:
            response?.data?.generalExaminationObservationDTO
              ?.obstetricFertilityTreatment || '',
        },
        erAdvice:
          response?.data?.gcsObservationDTO?.erAdvice?.map(
            (item: ERAdvice) => ({
              drug: item?.drug || '',
              medications: item?.medications || '',
            })
          ) || [],
      })
    }
    // eslint-disable-next-line
  }, [id])

  const generateComposition = async () => {
    const response = await saveComposition(id)
    if (response?.success) {
      setCompositionId(response?.data)
    } else {
      toast.error(response?.message)
    }
    return response?.data
  }

  const stepOneSubmit = async (
    values: InitialFormState,
    setSubmitting: any,
    compositionId: string
  ) => {
    const payload = {
      arrivalMode: values?.arrivalMode,
      arrivalNotification:
        values?.arrivalNotification === DECISION_OPTIONS?.[0]?.value
          ? true
          : false,
      hospitalName: values?.hospitalName,
      levelOfConsciousness: values?.levelOfConsciousness,
      pregnant:
        values?.pregnant === DECISION_OPTIONS?.[0]?.value ? true : false,
      allergies: values?.allergies,
      mlc: values?.mlc,
      broughtByName: values?.broughtBy?.name,
      broughtByRelationship: values?.broughtBy?.relationship,
      broughtByNumber: values?.broughtBy?.phoneNumber,
      pulse: values?.vitals?.pulse,
      spo2: values?.vitals?.spo2,
      bp: values?.vitals?.bp,
      rr: values?.vitals?.rr,
      grbs: values?.vitals?.grbs,
      temperature: values?.vitals?.temperature,
      gcs: values?.vitals?.gcs,
      painScore: values?.vitals?.painScore,
      presentingComplaints: values?.presentingComplaints,
      htn: values?.comorbidities?.htn,
      dm: values?.comorbidities?.dm,
      cva: values?.comorbidities?.cva,
      epilepsy: values?.comorbidities?.epilepsy,
      cad: values?.comorbidities?.cad,
      copd: values?.comorbidities?.copd,
      ckd: values?.comorbidities?.ckd,
      obesity: values?.comorbidities?.obesity,
    }

    const response = await postGeneralInitialObservation(
      payload,
      compositionId,
      id
    )
    if (response?.success) {
      setObservationOneId(response?.data?.observationId)
      setIsStepOneSubmit(response?.data?.isCompleted ? true : false)
      toast.success(response?.message)
      setSubmitting(false)
    } else {
      toast.error(response?.message)
      setSubmitting(false)
    }
  }

  const stepTwoSubmit = async (
    values: InitialFormState,
    setSubmitting: any
  ) => {
    const payload = {
      airway: values?.assessment?.airway,
      airwayPrimarySurvey: values?.assessment?.primarySurveyAirway,
      cervicalSpineImmobilization:
        values?.assessment?.cervicalSpineImmobilization,
      cervicalSpineImmobilizationSurvey:
        values?.assessment?.primarySurveyCervicalSpineImmobilization,
      other: values?.assessment?.other,
      capillaryRefillTime: values?.assessment?.circulation?.capillaryRefillTime,

      carotidR: values?.assessment?.circulation?.peripheralPulses?.carotidRight,
      carotidL: values?.assessment?.circulation?.peripheralPulses?.carotidLeft,
      brachialR:
        values?.assessment?.circulation?.peripheralPulses?.brachialRight,
      brachialL:
        values?.assessment?.circulation?.peripheralPulses?.brachialLeft,
      radialR: values?.assessment?.circulation?.peripheralPulses?.radialRight,
      radialL: values?.assessment?.circulation?.peripheralPulses?.radialLeft,
      femoralR: values?.assessment?.circulation?.peripheralPulses?.femoralRight,
      femoralL: values?.assessment?.circulation?.peripheralPulses?.femoralLeft,
      poplitealR:
        values?.assessment?.circulation?.peripheralPulses?.poplitealRight,
      poplitealL:
        values?.assessment?.circulation?.peripheralPulses?.poplitealLeft,
      tibalR: values?.assessment?.circulation?.peripheralPulses?.tibalRight,
      tibalL: values?.assessment?.circulation?.peripheralPulses?.tibalLeft,
      dorasilR: values?.assessment?.circulation?.peripheralPulses?.dorasilRight,
      dorasilL: values?.assessment?.circulation?.peripheralPulses?.dorasilLeft,

      o2: values?.assessment?.breathing?.o2,
      o2PrimarySurvey: values?.assessment?.breathing?.o2PrimarySurvey,
      niv: values?.assessment?.breathing?.niv,
      nivPrimarySurvey: values?.assessment?.breathing?.nivPrimarySurvey,
      nrbm: values?.assessment?.breathing?.nrbm,
      nrbmPrimarySurvey: values?.assessment?.breathing?.nrbmPrimarySurvey,
    }

    const response = await postBreathingObservation(
      payload,
      compositionId,
      id,
      observationOneId
    )
    if (response?.success) {
      setObservationTwoId(response?.data?.observationId)
      setIsStepTwoSubmit(response?.data?.isCompleted ? true : false)
      toast.success(response?.message)
      setSubmitting(false)
    } else {
      toast.error(response?.message)
      setSubmitting(false)
    }
  }

  const stepThreeSubmit = async (
    values: InitialFormState,
    setSubmitting: any
  ) => {
    const payload = {
      gcsE: values?.gcs?.e,
      gcsV: values?.gcs?.v,
      gcsM: values?.gcs?.m,
      pupilsRight: values?.pupils?.right,
      pupilsLeft: values?.pupils?.left,
      fnd: values?.fnd,
      exposure: values?.exposure,
      chiefComplaints: values?.chiefComplaints,
      previousMedications:
        values?.previousMedications?.map((item: Medication) => ({
          drug: item?.drug || '',
          dose: item?.dose || '',
          frequency: item?.frequency || '',
          lastDoseDate: item?.lastDoseDate || '',
          lastDoseTime: item?.lastDoseTime || '',
        })) || [],
      erAdvice:
        values?.erAdvice?.map((item: ERAdvice) => ({
          drug: item?.drug || '',
          medications: item?.medications || '',
        })) || [],
    }
    const response = await postGcsObservation(
      payload,
      compositionId,
      id,
      observationOneId,
      observationTwoId
    )
    if (response?.success) {
      setObservationThreeId(response?.data?.observationId)
      setMedicationStatementIdOne(
        response?.data?.previousMedications?.[0]?.previousMedicationId
      )
      setMedicationStatementIdTwo(response?.data?.erAdvice?.[0]?.erAdviceId)
      setIsStepThreeSubmit(response?.data?.isCompleted ? true : false)
      toast.success(response?.message)
      setSubmitting(false)
    } else {
      toast.error(response?.message)
      setSubmitting(false)
    }
  }

  const stepFourSubmit = async (
    values: InitialFormState,
    setSubmitting: any
  ) => {
    const payload = {
      // General Examination
      pallor: values?.generalExamination?.pallor,
      icterus: values?.generalExamination?.icterus,
      cyanosis: values?.generalExamination?.cyanosis,
      clubbing: values?.generalExamination?.clubbing,
      lymphadenopathy: values?.generalExamination?.lymphadenopathy,
      edema: values?.generalExamination?.edema,

      // ENT
      earAche: values?.reviewOfSystems?.ENT?.earAche,
      earDischarge: values?.reviewOfSystems?.ENT?.earDischarge,
      tinnitus: values?.reviewOfSystems?.ENT?.tinnitus,
      vertigo: values?.reviewOfSystems?.ENT?.vertigo,
      voiceChange: values?.reviewOfSystems?.ENT?.voiceChange,
      soreThroat: values?.reviewOfSystems?.ENT?.soreThroat,
      swellingNeck: values?.reviewOfSystems?.ENT?.swellingNeck,
      difficultyInSwallowing:
        values?.reviewOfSystems?.ENT?.difficultySwallowing,
      entNoSymptoms: values?.reviewOfSystems?.ENT?.noSymptoms,

      // Ophthalmology
      eyeRedness: values?.reviewOfSystems?.Ophthalmology?.eyeRedness,
      visionLoss: values?.reviewOfSystems?.Ophthalmology?.visionLoss,
      eyePain: values?.reviewOfSystems?.Ophthalmology?.eyePain,
      eyeDischarge: values?.reviewOfSystems?.Ophthalmology?.eyeDischarge,
      ophthalmologyNoSymptoms:
        values?.reviewOfSystems?.Ophthalmology?.noSymptoms,

      // Cardiovascular
      shortnessOfBreath:
        values?.reviewOfSystems?.Cardiovascular?.shortnessOfBreath,
      chestPain: values?.reviewOfSystems?.Cardiovascular?.chestPain,
      oedema: values?.reviewOfSystems?.Cardiovascular?.oedema,
      palpitation: values?.reviewOfSystems?.Cardiovascular?.palpitation,
      cvNoSymptoms: values?.reviewOfSystems?.Cardiovascular?.noSymptoms,

      // Respiratory
      hemoptysis: values?.reviewOfSystems?.Respiratory?.hemoptysis,
      cough: values?.reviewOfSystems?.Respiratory?.cough,
      sputum: values?.reviewOfSystems?.Respiratory?.sputum,
      wheeze: values?.reviewOfSystems?.Respiratory?.wheeze,
      respNoSymptoms: values?.reviewOfSystems?.Respiratory?.noSymptoms,

      // GI
      abdominalPain: values?.reviewOfSystems?.Gastrointestinal?.abdominalPain,
      vomiting: values?.reviewOfSystems?.Gastrointestinal?.vomiting,
      malena: values?.reviewOfSystems?.Gastrointestinal?.malena,
      hepatitisBC: values?.reviewOfSystems?.Gastrointestinal?.hepatitisBC,
      diarrhea: values?.reviewOfSystems?.Gastrointestinal?.diarrhea,
      hematemesis: values?.reviewOfSystems?.Gastrointestinal?.hematemesis,
      flatus: values?.reviewOfSystems?.Gastrointestinal?.flatus,
      jaundice: values?.reviewOfSystems?.Gastrointestinal?.jaundice,
      constipation: values?.reviewOfSystems?.Gastrointestinal?.constipation,
      bleedingPR: values?.reviewOfSystems?.Gastrointestinal?.bleedingPR,
      giAnyOther: values?.reviewOfSystems?.Gastrointestinal?.anyOther,
      giNoSymptoms: values?.reviewOfSystems?.Gastrointestinal?.noSymptoms,

      // Genitourinary
      guHematuria: values?.reviewOfSystems?.Genitourinary?.hematuria,
      guHoTurp: values?.reviewOfSystems?.Genitourinary?.historyOfTURP,
      guDialysis: values?.reviewOfSystems?.Genitourinary?.dialysis,
      guDischarge:
        values?.reviewOfSystems?.Genitourinary?.penileVaginalDischarge,
      guDysuria: values?.reviewOfSystems?.Genitourinary?.dysuria,
      guHoStones: values?.reviewOfSystems?.Genitourinary?.historyOfStones,
      guInfertility: values?.reviewOfSystems?.Genitourinary?.infertility,
      guNocturia: values?.reviewOfSystems?.Genitourinary?.nocturia,
      guRenalFailure: values?.reviewOfSystems?.Genitourinary?.renalFailure,
      guAnyOther: values?.reviewOfSystems?.Genitourinary?.anyOther,
      guNoSymptoms: values?.reviewOfSystems?.Genitourinary?.noSymptoms,

      // Musculoskeletal
      msJointPain: values?.reviewOfSystems?.Musculoskeletal?.jointPain,
      msJointSwelling: values?.reviewOfSystems?.Musculoskeletal?.jointSwelling,
      msInabilityToWalk:
        values?.reviewOfSystems?.Musculoskeletal?.inabilityToWalk,
      msMusclePain: values?.reviewOfSystems?.Musculoskeletal?.musclePain,
      msFracture: values?.reviewOfSystems?.Musculoskeletal?.fracture,
      msJointStiffness:
        values?.reviewOfSystems?.Musculoskeletal?.jointStiffness,
      msTrauma: values?.reviewOfSystems?.Musculoskeletal?.trauma,
      msNoSymptoms: values?.reviewOfSystems?.Musculoskeletal?.noSymptoms,

      // Neurological
      neuroHeadache: values?.reviewOfSystems?.Neurological?.headache,
      neuroEarDischarge: values?.reviewOfSystems?.Neurological?.earDischarge,
      neuroTinnitus: values?.reviewOfSystems?.Neurological?.tinnitus,
      neuroVertigo: values?.reviewOfSystems?.Neurological?.vertigo,
      neuroNumbness: values?.reviewOfSystems?.Neurological?.numbness,
      neuroSyncope: values?.reviewOfSystems?.Neurological?.syncope,
      neuroBackAche: values?.reviewOfSystems?.Neurological?.backAche,
      neuroDiplopia: values?.reviewOfSystems?.Neurological?.diplopia,
      neuroRadiatingPain: values?.reviewOfSystems?.Neurological?.radiatingPain,
      neuroDrowsiness: values?.reviewOfSystems?.Neurological?.drowsiness,
      neuroWeaknessLimbs:
        values?.reviewOfSystems?.Neurological?.weaknessInLimbs,
      neuroStroke: values?.reviewOfSystems?.Neurological?.stroke,
      neuroClaudication: values?.reviewOfSystems?.Neurological?.claudication,
      neuroRestPainLegs: values?.reviewOfSystems?.Neurological?.restPainInLegs,
      neuroNoSymptoms: values?.reviewOfSystems?.Neurological?.noSymptoms,

      // Integumentary
      integSkinDisorders: values?.reviewOfSystems?.Integumentary?.skinDisorders,
      integRash: values?.reviewOfSystems?.Integumentary?.rash,
      integItching: values?.reviewOfSystems?.Integumentary?.itching,
      integDiscoloration: values?.reviewOfSystems?.Integumentary?.discoloration,
      integOthers: values?.reviewOfSystems?.Integumentary?.others,
      integNoSymptoms: values?.reviewOfSystems?.Integumentary?.noSymptoms,

      // Psychiatric
      psyAgitated: values?.reviewOfSystems?.Psychiatric?.agitated,
      psyDemented: values?.reviewOfSystems?.Psychiatric?.demented,
      psyAnxious: values?.reviewOfSystems?.Psychiatric?.anxious,
      psyDepression: values?.reviewOfSystems?.Psychiatric?.depression,
      psyNoSymptoms: values?.reviewOfSystems?.Psychiatric?.noSymptoms,

      // Endocrine
      endoThyroidSwelling:
        values?.reviewOfSystems?.[REVIEW_OF_SYSTEMS_CONST?.ENDOCRINE]
          ?.thyroidSwelling,
      endoDiabetes:
        values?.reviewOfSystems?.[REVIEW_OF_SYSTEMS_CONST?.ENDOCRINE]?.diabetes,
      endoAdrenalMass:
        values?.reviewOfSystems?.[REVIEW_OF_SYSTEMS_CONST?.ENDOCRINE]
          ?.adrenalMass,
      endoPituitaryLesion:
        values?.reviewOfSystems?.[REVIEW_OF_SYSTEMS_CONST?.ENDOCRINE]
          ?.pituitaryLesion,
      endoNoSymptoms:
        values?.reviewOfSystems?.[REVIEW_OF_SYSTEMS_CONST?.ENDOCRINE]
          ?.noSymptoms,

      // Hematologic
      hemAnemia:
        values?.reviewOfSystems?.[REVIEW_OF_SYSTEMS_CONST?.HEMATOLOGIC]?.anemia,
      hemLowPlatelets:
        values?.reviewOfSystems?.[REVIEW_OF_SYSTEMS_CONST?.HEMATOLOGIC]
          ?.lowPlatelets,
      hemNeutropenia:
        values?.reviewOfSystems?.[REVIEW_OF_SYSTEMS_CONST?.HEMATOLOGIC]
          ?.neutropenia,
      hemBloodTransfusions:
        values?.reviewOfSystems?.[REVIEW_OF_SYSTEMS_CONST?.HEMATOLOGIC]
          ?.bloodTransfusions,
      hemNoSymptoms:
        values?.reviewOfSystems?.[REVIEW_OF_SYSTEMS_CONST?.HEMATOLOGIC]
          ?.noSymptoms,

      // Provisional & Obstetric
      provisionalDiagnosis: values?.provisionalDiagnosis,
      obstetricYes:
        values?.obstetric?.obstetricValue === OBSTETRIC_RADIO_OPTIONS?.[0].value
          ? true
          : false,
      obstetricGravidity: values?.obstetric?.gravidity,
      obstetricParity: values?.obstetric?.parity,
      obstetricParityDetails: values?.obstetric?.parityDetails,
      obstetricLmp: values?.obstetric?.lastMenstrualPeriod,
      obstetricLmpDays: `${values?.obstetric?.days}`,
      obstetricContraceptionHistory:
        values?.obstetric?.historyOfContraception ===
        DECISION_OPTIONS?.[0].value
          ? true
          : false,
      obstetricContraceptionDetails: values?.obstetric?.contraceptionDetails,
      obstetricFertilityTreatment: values?.obstetric?.fertilityTreatment,
      obstetricDeliveryType: values?.obstetric?.obstetricOptions,
      obstetricDeliveryDetails: values?.obstetric?.obstetricOptionDetails,
    }

    const response = await postGeneralExaminationObservation(
      payload,
      compositionId,
      id,
      observationOneId,
      observationTwoId,
      observationThreeId,
      medicationStatementIdOne,
      medicationStatementIdTwo
    )
    if (response?.success) {
      setObservationFourId(response?.data?.observationId)
      setIsStepFourSubmit(response?.data?.isCompleted ? true : false)
      toast.success(response?.message)
      setSubmitting(false)
    } else {
      toast.error(response?.message)
      setSubmitting(false)
    }
  }

  const handleSubmit = async (values: InitialFormState, setSubmitting: any) => {
    switch (activeStep) {
      case 0:
        if (compositionId) {
          stepOneSubmit(values, setSubmitting, compositionId)
        } else {
          const compositionId = await generateComposition() // wait for composition generation
          if (compositionId) {
            stepOneSubmit(values, setSubmitting, compositionId) // only proceed if successful
          }
        }
        break

      case 1:
        stepTwoSubmit(values, setSubmitting)
        break

      case 2:
        stepThreeSubmit(values, setSubmitting)
        break

      case 3:
        stepFourSubmit(values, setSubmitting)
        break

      default:
        break
    }
  }

  const disableSubmit = () => {
    switch (activeStep) {
      case 0:
        return isStepOneSubmit

      case 1:
        return isStepTwoSubmit

      case 2:
        return isStepThreeSubmit

      case 3:
        return isStepFourSubmit

      default:
        return false
    }
  }

  React.useEffect(() => {
    getFormData()
  }, [getFormData])

  return (
    <div className={classes?.initialAssessmentContainer}>
      <Box className={classes?.stepperContainer}>
        <Stepper
          activeStep={activeStep}
          className={classes?.initialAssessmentStepper}
        >
          {steps?.map((label) => {
            const stepProps: { completed?: boolean } = {}
            const labelProps: {
              optional?: React.ReactNode
            } = {}
            return (
              <Step key={label} {...stepProps}>
                <StepLabel {...labelProps}>{}</StepLabel>
              </Step>
            )
          })}
        </Stepper>

        <React.Fragment>
          <Box
            mt={0.5}
            mb={1}
            width='100%'
            display='flex'
            justifyContent='center'
            alignItems='center'
            padding={3}
            borderRadius={5}
            bgcolor={'#ffffff'}
          >
            {renderStep()}
          </Box>
          <Box
            display='flex'
            flexDirection='row'
            pt={2}
            justifyContent={'space-between'}
          >
            <Button
              size='small'
              disabled={activeStep === 0}
              onClick={handleBack}
              variant='contained'
              startIcon={<ArrowBackIosIcon fontSize='small' />}
              className={classes?.initialAssessmentButton}
            >
              Back
            </Button>
            <Button
              size='small'
              onClick={initialAssessmentForm.submitForm}
              variant='contained'
              endIcon={<CloudDoneIcon fontSize='small' />}
              className={classes?.initialAssessmentButton}
              disabled={initialAssessmentForm?.isSubmitting || disableSubmit()}
            >
              Submit
            </Button>
            <Button
              size='small'
              onClick={handleNext}
              variant='contained'
              endIcon={<ArrowForwardIosIcon fontSize='small' />}
              className={classes?.initialAssessmentButton}
              disabled={activeStep === 3 || !disableSubmit()}
            >
              Next
            </Button>
          </Box>
        </React.Fragment>
      </Box>
    </div>
  )
}
